import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { debounce, isEqual, isNull, mapKeys, uniq } from 'lodash';
import moment from 'moment-timezone';
import { Waypoint } from 'react-waypoint';

import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import { PrimaryButton } from '@magoosh/lib/button';
import Dropdown from '@magoosh/lib/dropdown';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import AnswersTable from '@magoosh/organisms/answers_table';
import AnswerFlag from '@magoosh/organisms/answer_flag';
import AnswerIcon from '@magoosh/organisms/answer_icon';
import NoValueDash from '@magoosh/organisms/no_value_dash';
import { paths } from 'config/path_helpers';
import { AnswerData } from 'types/answer';
import { secondsFormattedAsMinutes } from 'utilities/time';

import ContextualLink from '@test_prep/app/components/contextual_link';
import * as api from '@test_prep/app/api';

import style from './style.module.scss';

interface Props {
  answers: AnswerData[]
  filters: any;
  onChange: (answers: AnswerData[]) => void;
}

export default (props: Props) => {
  const [page, setPage] = useState<number>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const restoredFromHistory = useRef(false);

  const answerPath = (answerId: number) => {
    const filtersForUrl = mapKeys(props.filters, (value, key) => `filters[${key}]`);
    return paths.answer(answerId, filtersForUrl);
  };

  // Ensure that if the user navigates away and then back we resume with the same pages of answers
  // and scroll position.
  const saveToHistoryState = (answers, answersPage) => {
    window.history.replaceState({ answers, answersPage, scrollY: window.scrollY }, '');
  };

  const loadAnswers = (pageToFetch, clearAnswers = false) => {
    setIsLoading(true);
    if (clearAnswers) props.onChange([]);

    api.fetchAnswers(props.filters, pageToFetch).then((loadedAnswers) => {
      const newPage = loadedAnswers.length === 25 ? pageToFetch : null;
      setPage(newPage);

      const updatedAnswers = clearAnswers
        ? loadedAnswers
        : [...props.answers, ...loadedAnswers];

      props.onChange(updatedAnswers);
      saveToHistoryState(updatedAnswers, newPage);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    const historyState = window.history.state as any;
    const navType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type;
    const isBackForwardNavigation = navType === 'back_forward' || window.history.state?.__reactRouterPop;

    if (!restoredFromHistory.current && isBackForwardNavigation && historyState?.answers) {
      restoredFromHistory.current = true;
      props.onChange(historyState.answers);
      setPage(historyState.answersPage);
      requestAnimationFrame(() => window.scrollTo(0, historyState.scrollY || 0));
    } else {
      restoredFromHistory.current = true;
      window.history.replaceState(null, '');
      loadAnswers(1, true);
    }
  }, [props.filters]);

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (window.history.state?.answers) {
        window.history.replaceState({ ...window.history.state, scrollY: window.scrollY }, '');
      }
    }, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <AnswersTable
      answers={props.answers}
      isLoading={isLoading}
      linkComponent={ContextualLink}
      onLoadMoreAnswers={() => loadAnswers(page + 1)}
      pathForAnswer={answerPath}
    />
  );
};
